import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import CardHeader from "@mui/material/CardHeader"
import CircularProgress from "@mui/material/CircularProgress"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useEffect, useRef, useState } from "react"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import Row, { RowButColumnOnMobile } from "../../../common/components/Row"
import AiProfileGenerationDialog, {
  outlinedInputBorderSx,
} from "./AiProfileGenerationDialog"
import {
  BROKERAGE_PROFILE_INTAKE_API_PATH,
  RECRUITING_CAMPAIGN_PROFILE_TITLE,
  WEBSITE_ABOUT_OR_RECRUITING_PAGE_LABEL,
  intakeCamelToSnakeApi,
  mergeWebsitePrefillWithSavedIntake,
  validateBrokerageWebsiteUrl,
  writeIntakeSnapshot,
} from "./brokerageProfileIntakeFields"
import { SETTINGS_SEARCH_PARAMS } from "./campaignNavigation"
import { useBrokerageProfileIntake } from "./useBrokerageProfileIntake"

export { BROKERAGE_PROFILE_INTAKE_API_PATH }

/** POST body / response align with `clients:api:profile_generate_from_website`. */
export const PROFILE_GENERATE_FROM_WEBSITE_API_PATH =
  "/clients/api/profile/generate-from-website/"

function formatRequestError(data) {
  if (!data || typeof data !== "object") {
    return "Something went wrong. Please try again."
  }
  if (typeof data.detail === "string") {
    return data.detail
  }
  const website = data.website_url
  if (Array.isArray(website) && website.length) {
    return website[0]
  }
  if (typeof website === "string") {
    return website
  }
  return "Something went wrong. Please try again."
}

/**
 * Brokerage website URL → generate profile flow (submit on Profile tab).
 *
 * @param {object} props
 * @param {() => void | Promise<void>} [props.onProfileSaved] After Submit (Airtable sync).
 * @param {() => void} [props.onProfileDraftSaved] After Save (database only).
 */
export default function AiProfileFromWebsiteCard({
  onProfileSaved,
  onProfileDraftSaved,
  onViewCampaign,
}) {
  const axios = useAuthorizedAxiosClient()
  const {
    isLoading: isLoadingSaved,
    intake: savedIntake,
    hasSubmitted: showSubmittedSummary,
    applyPayload,
  } = useBrokerageProfileIntake()
  const [websiteInput, setWebsiteInput] = useState("")
  const [urlFieldError, setUrlFieldError] = useState(null)
  const [requestError, setRequestError] = useState(null)
  const [isOpeningDialog, setIsOpeningDialog] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogLoading, setDialogLoading] = useState(false)
  const [dialogPrefill, setDialogPrefill] = useState(null)
  const [dialogApiError, setDialogApiError] = useState(null)
  const [dialogWebsiteUrl, setDialogWebsiteUrl] = useState("")
  const ignoreFetchResultRef = useRef(false)
  const openedFromRedirectRef = useRef(false)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()

  const websiteValidation = validateBrokerageWebsiteUrl(websiteInput)
  const urlLooksValid = websiteValidation.valid

  useEffect(() => {
    if (savedIntake?.websiteUrl) {
      setWebsiteInput(savedIntake.websiteUrl)
    }
  }, [savedIntake?.websiteUrl])

  function handleWebsiteChange(e) {
    const value = e.target.value
    setWebsiteInput(value)
    setRequestError(null)
    if (!value.trim()) {
      setUrlFieldError(null)
      return
    }
    const check = validateBrokerageWebsiteUrl(value)
    setUrlFieldError(check.valid ? null : check.message)
  }

  function handleWebsiteBlur() {
    const check = validateBrokerageWebsiteUrl(websiteInput)
    setUrlFieldError(check.valid ? null : check.message)
  }

  function clearRecruitingProfileRedirectParam() {
    const next = new URLSearchParams(searchParams)
    next.delete(SETTINGS_SEARCH_PARAMS.openRecruitingProfile)
    const qs = next.toString()
    navigate(qs ? `${location.pathname}?${qs}` : location.pathname, {
      replace: true,
    })
  }

  function openProfileDialogFromRedirect() {
    if (openedFromRedirectRef.current) {
      return false
    }

    const savedSnake = savedIntake ? intakeCamelToSnakeApi(savedIntake) : null
    if (showSubmittedSummary && savedSnake) {
      const url = savedIntake.websiteUrl || websiteInput
      const check = validateBrokerageWebsiteUrl(url)
      setDialogWebsiteUrl(check.valid ? check.normalized : (url || "").trim())
      setDialogPrefill(savedSnake)
      setDialogApiError(null)
      setDialogOpen(true)
      setDialogLoading(false)
      openedFromRedirectRef.current = true
      clearRecruitingProfileRedirectParam()
      return true
    }

    const check = validateBrokerageWebsiteUrl(websiteInput)
    if (check.valid) {
      openedFromRedirectRef.current = true
      clearRecruitingProfileRedirectParam()
      setDialogWebsiteUrl(check.normalized)
      setDialogApiError(null)
      setDialogOpen(true)
      ignoreFetchResultRef.current = false

      if (savedSnake) {
        setDialogPrefill(savedSnake)
        setDialogLoading(false)
      } else {
        setIsOpeningDialog(true)
        setDialogPrefill(null)
        setDialogLoading(true)
        axios
          .post(PROFILE_GENERATE_FROM_WEBSITE_API_PATH, {
            website_url: check.normalized,
          })
          .then(({ data }) => {
            if (ignoreFetchResultRef.current) {
              return
            }
            setDialogPrefill(
              mergeWebsitePrefillWithSavedIntake(
                data && typeof data === "object" ? data : null,
                savedIntake
              )
            )
          })
          .catch((err) => {
            if (ignoreFetchResultRef.current) {
              return
            }
            const data = err.response?.data
            setDialogApiError(formatRequestError(data))
            if (savedSnake) {
              setDialogPrefill(savedSnake)
            }
          })
          .finally(() => {
            if (!ignoreFetchResultRef.current) {
              setDialogLoading(false)
            }
            setIsOpeningDialog(false)
          })
      }
      return true
    }

    return false
  }

  useEffect(() => {
    if (
      searchParams.get(SETTINGS_SEARCH_PARAMS.openRecruitingProfile) !== "1"
    ) {
      openedFromRedirectRef.current = false
      return
    }
    if (isLoadingSaved) {
      return
    }
    openProfileDialogFromRedirect()
  }, [
    isLoadingSaved,
    searchParams,
    showSubmittedSummary,
    savedIntake,
    websiteInput,
  ])

  function handleGenerateClicked() {
    const check = validateBrokerageWebsiteUrl(websiteInput)
    if (!check.valid) {
      setUrlFieldError(check.message)
      return
    }
    setRequestError(null)
    setDialogWebsiteUrl(check.normalized)
    setDialogApiError(null)
    setDialogOpen(true)
    ignoreFetchResultRef.current = false

    const savedSnake = savedIntake ? intakeCamelToSnakeApi(savedIntake) : null
    const isUpdatingExistingProfile = Boolean(
      showSubmittedSummary && savedSnake
    )

    if (isUpdatingExistingProfile) {
      setDialogPrefill(savedSnake)
      setDialogLoading(false)
      setIsOpeningDialog(false)
      return
    }

    setIsOpeningDialog(true)
    setDialogPrefill(savedSnake)
    setDialogLoading(true)

    axios
      .post(PROFILE_GENERATE_FROM_WEBSITE_API_PATH, {
        website_url: check.normalized,
      })
      .then(({ data }) => {
        if (ignoreFetchResultRef.current) {
          return
        }
        setDialogPrefill(
          mergeWebsitePrefillWithSavedIntake(
            data && typeof data === "object" ? data : null,
            savedIntake
          )
        )
      })
      .catch((err) => {
        if (ignoreFetchResultRef.current) {
          return
        }
        const data = err.response?.data
        setDialogApiError(formatRequestError(data))
        if (savedSnake) {
          setDialogPrefill(savedSnake)
        }
      })
      .finally(() => {
        if (!ignoreFetchResultRef.current) {
          setDialogLoading(false)
        }
        setIsOpeningDialog(false)
      })
  }

  function handleDialogClose() {
    ignoreFetchResultRef.current = true
    setDialogOpen(false)
    setDialogLoading(false)
    setDialogPrefill(null)
    setDialogApiError(null)
  }

  async function persistProfileForm(
    payload,
    { navigateToCampaign = false, onSuccess } = {}
  ) {
    setIsSubmitting(true)
    setRequestError(null)
    try {
      const { data } = await axios.post(
        BROKERAGE_PROFILE_INTAKE_API_PATH,
        payload
      )
      if (data?.intake) {
        writeIntakeSnapshot({
          submitted: Boolean(data.submitted),
          submitted_at: data.submitted_at,
          intake: data.intake,
        })
        applyPayload({
          submitted: data.submitted,
          submitted_at: data.submitted_at,
          intake: data.intake,
        })
      }
      if (navigateToCampaign && onProfileSaved) {
        await onProfileSaved(data, payload)
      } else if (onSuccess) {
        onSuccess(data, payload)
      }
      return data
    } catch (err) {
      const data = err.response?.data
      const detail =
        typeof data?.detail === "string"
          ? data.detail
          : "Could not save brokerage profile. Please try again."
      setRequestError(detail)
      throw err
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleProfileFormSave(payload) {
    await persistProfileForm(payload, {
      navigateToCampaign: false,
      onSuccess: () => {
        if (onProfileDraftSaved) {
          onProfileDraftSaved()
        }
      },
    })
  }

  async function handleProfileFormSubmit(payload) {
    await persistProfileForm(payload, { navigateToCampaign: true })
  }

  return (
    <Card variant="outlined" sx={{ width: "100%" }}>
      <CardHeader
        title={RECRUITING_CAMPAIGN_PROFILE_TITLE}
        titleTypographyProps={{ variant: "subtitle1", fontWeight: 700 }}
        subheader="Add your brokerage website, complete your profile details, and submit."
        sx={{
          textAlign: "left",
          alignItems: "flex-start",
          pb: 0.5,
          pt: 1.5,
          px: 2,
          "& .MuiCardHeader-content": { overflow: "visible" },
        }}
        subheaderTypographyProps={{
          variant: "caption",
          sx: { textAlign: "left", mt: 0.25, color: "text.secondary" },
        }}
      />
      <CardContent
        sx={{
          pt: 0,
          px: 2,
          pb: 1.5,
          "&:last-child": { pb: 1.5 },
        }}
      >
        <Column sx={{ gap: 1.25, alignItems: "stretch" }}>
          {requestError ? (
            <Alert severity="error" onClose={() => setRequestError(null)}>
              {requestError}
            </Alert>
          ) : null}
          <RowButColumnOnMobile sx={{ gap: 1.5, alignItems: "flex-start" }}>
            <TextField
              variant="outlined"
              sx={{ ...outlinedInputBorderSx, flex: "2 1 280px" }}
              label={WEBSITE_ABOUT_OR_RECRUITING_PAGE_LABEL}
              placeholder="https://www.yourbrokerage.com/your-page"
              size="small"
              value={websiteInput}
              onChange={handleWebsiteChange}
              onBlur={handleWebsiteBlur}
              required
              error={!!urlFieldError}
              helperText={
                urlFieldError ||
                "Required. https:// is added automatically if you omit it."
              }
              disabled={isOpeningDialog || isSubmitting}
            />
            <Row
              sx={{
                flex: "1 1 auto",
                flexWrap: "wrap",
                gap: 1,
                alignItems: "center",
                pt: { xs: 0, sm: 0.25 },
              }}
            >
              <Button
                variant="outlined"
                size="small"
                onClick={handleGenerateClicked}
                disabled={isOpeningDialog || isSubmitting || !urlLooksValid}
              >
                {isOpeningDialog ? (
                  <Row sx={{ gap: 1, alignItems: "center" }}>
                    <CircularProgress color="inherit" size={16} />
                    <span>Opening…</span>
                  </Row>
                ) : showSubmittedSummary ? (
                  "Update campaign profile"
                ) : (
                  "Create profile"
                )}
              </Button>
              {showSubmittedSummary && onViewCampaign ? (
                <Button
                  variant="contained"
                  size="small"
                  onClick={onViewCampaign}
                >
                  View Campaign
                </Button>
              ) : null}
            </Row>
          </RowButColumnOnMobile>
          <Typography variant="caption" color="text.secondary">
            {isLoadingSaved
              ? "Loading your saved profile…"
              : showSubmittedSummary
              ? "Your profile is saved. Open the Campaign tab to view or update your details."
              : "After you submit, your full profile will be available on the Campaign tab."}
          </Typography>
        </Column>
      </CardContent>

      <AiProfileGenerationDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        websiteUrl={dialogWebsiteUrl}
        initialFromApi={dialogPrefill}
        isLoadingApi={dialogLoading}
        apiError={dialogApiError}
        isSubmitting={isSubmitting}
        onSave={handleProfileFormSave}
        onSubmit={handleProfileFormSubmit}
      />
    </Card>
  )
}
